import { useEffect, useMemo, useRef, useState } from "react"
import { ForwardArrowIcon } from "./Icons"
import { csrfToken } from "../lib/rails"
import { AgentActivityFeed, formatEventText } from "./AgentActivityFeed"

const initialState = {
  buttonText: "Map architecture",
  events: [],
  linksVisible: false,
  status: "Reading repository signals.",
  streamVisible: false,
  working: false
}

export function mergeArchitectureEvents(currentEvents = [], incomingEvents = []) {
  const mergedEvents = currentEvents.map((event) => normalizeEvent(event)).filter(Boolean)
  const seenSequences = new Set(mergedEvents.map((event) => event.sequence).filter(Boolean))

  incomingEvents.forEach((event) => {
    const normalized = normalizeEvent(event)
    if (!normalized) return
    if (normalized.sequence && seenSequences.has(normalized.sequence)) return

    const adjacentEvent = mergedEvents[mergedEvents.length - 1]
    const mergeKey = architectureEventDeltaMergeKey(normalized)
    if (!mergeKey && isDuplicateArchitectureEvent(adjacentEvent, normalized)) return

    if (normalized.sequence) seenSequences.add(normalized.sequence)
    if (mergeKey && adjacentEvent && architectureEventDeltaMergeKey(adjacentEvent) === mergeKey) {
      mergedEvents[mergedEvents.length - 1] = {
        ...adjacentEvent,
        ...normalized,
        key: adjacentEvent.key,
        rawType: normalized.rawType || adjacentEvent.rawType,
        sequence: normalized.sequence || adjacentEvent.sequence,
        text: `${adjacentEvent.text || ""}${normalized.text || ""}`
      }
      return
    }

    mergedEvents.push(normalized)
  })

  return mergedEvents
}

function isDuplicateArchitectureEvent(previousEvent, event) {
  if (!previousEvent || !event) return false

  const previousRawType = previousEvent.rawType || previousEvent.raw_type
  const rawType = event.rawType || event.raw_type
  const rawTypesMatch = !previousRawType || !rawType || String(previousRawType) === String(rawType)

  return String(previousEvent.type || "") === String(event.type || "") &&
    String(previousEvent.text || "") === String(event.text || "") &&
    rawTypesMatch
}

function architectureEventDeltaMergeKey(event) {
  const rawType = event?.rawType || event?.raw_type
  if (!rawType) return null

  const value = String(rawType)
  if (!value.match(/(?:agent_message_delta|assistant_message_delta|message_delta|reasoning_delta|content_delta|text_delta)$/i)) return null

  return value.replace(/(?:agent_message_delta|assistant_message_delta|message_delta|reasoning_delta|content_delta|text_delta)$/i, "delta")
}

export function ArchitectureConsole({
  architecture,
  autoStart = false,
  developerToolsEnabled = false,
  onComplete,
  onContinue,
  onStreamLinesChange,
  visible = true
}) {
  const autoStartedRef = useRef(false)
  const continueIntroTimerRef = useRef(null)
  const continueIntroPlayedRef = useRef(false)
  const lastSequenceRef = useRef(0)
  const pollRef = useRef(null)
  const [continueEnabled, setContinueEnabled] = useState(architecture.available)
  const [state, setState] = useState({
    ...initialState,
    buttonText: architecture.available && developerToolsEnabled ? "Replay mapping" : initialState.buttonText,
    linksVisible: architecture.available,
    status: architecture.available ? "Architecture map ready" : "Reading repository signals.",
    streamVisible: !architecture.available,
    links: {
      markdown: architecture.paths.markdown
    }
  })

  useEffect(() => {
    if (!autoStart || architecture.available || autoStartedRef.current) return

    autoStartedRef.current = true
    startGeneration()
  }, [autoStart, architecture.available])

  useEffect(() => {
    if (architecture.available || !autoStart || continueIntroPlayedRef.current) return

    continueIntroPlayedRef.current = true
    startContinueIntro()
  }, [architecture.available, autoStart])

  useEffect(() => () => {
    stopEventPolling()
    if (continueIntroTimerRef.current) clearTimeout(continueIntroTimerRef.current)
  }, [])

  function appendEvent(event) {
    setState((current) => ({
      ...current,
      events: mergeArchitectureEvents(current.events, [event])
    }))
  }

  function appendEvents(events) {
    if (events.length === 0) return

    setState((current) => ({
      ...current,
      events: mergeArchitectureEvents(current.events, events)
    }))
  }

  function stopEventPolling() {
    if (!pollRef.current) return

    clearInterval(pollRef.current)
    pollRef.current = null
  }

  function completeGeneration(payload) {
    stopEventPolling()
    setState((current) => ({
      ...current,
      buttonText: developerToolsEnabled ? "Replay mapping" : current.buttonText,
      links: {
        markdown: payload.markdown_url || current.links.markdown
      },
      linksVisible: true,
      status: "Architecture map ready",
      working: false
    }))
    if (onComplete) onComplete(payload)
  }

  function failGeneration(message = "Mapping failed", recordEvent = true) {
    stopEventPolling()
    setState((current) => ({
      ...current,
      buttonText: "Try again",
      status: "Mapping failed",
      working: false
    }))
    if (recordEvent) appendEvent({ type: "error", text: message })
  }

  function startContinueIntro() {
    if (continueIntroTimerRef.current) clearTimeout(continueIntroTimerRef.current)

    setContinueEnabled(false)
    continueIntroTimerRef.current = window.setTimeout(() => {
      setContinueEnabled(true)
      continueIntroTimerRef.current = null
    }, 4000)
  }

  function eventsUrl() {
    const url = new URL(architecture.paths.events, window.location.origin)
    url.searchParams.set("after", lastSequenceRef.current)
    return url.toString()
  }

  function handleGenerationStatus(payload, events = []) {
    const hasEvent = (type) => events.some((event) => event.type === type)

    if (payload.status === "completed" || payload.available) {
      completeGeneration(payload)
      return true
    }

    if (payload.status === "failed") {
      failGeneration(payload.error_message || "Mapping failed", !hasEvent("error"))
      return true
    }

    if (payload.status === "stopped") {
      failGeneration("Mapping stopped", !hasEvent("stopped"))
      return true
    }

    setState((current) => ({
      ...current,
      buttonText: "Mapping architecture",
      status: "Reading repository signals.",
      streamVisible: true,
      working: true
    }))

    return false
  }

  function applyEventsPayload(payload) {
    const events = Array.isArray(payload.events) ? payload.events : []

    if (events.length > 0) {
      lastSequenceRef.current = events[events.length - 1].sequence || lastSequenceRef.current
      appendEvents(events)
    } else if (payload.next_sequence !== undefined) {
      lastSequenceRef.current = Number(payload.next_sequence) || lastSequenceRef.current
    }

    return handleGenerationStatus(payload, events)
  }

  function pollGenerationEvents() {
    if (!architecture.paths.events) {
      failGeneration("Could not read mapping events.")
      return
    }

    fetch(eventsUrl(), {
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-store"
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not read mapping events.")
        return response.json()
      })
      .then(applyEventsPayload)
      .catch((error) => {
        failGeneration(error.message)
      })
  }

  function startEventPolling() {
    stopEventPolling()
    pollGenerationEvents()
    pollRef.current = window.setInterval(pollGenerationEvents, 1000)
  }

  function startGeneration() {
    stopEventPolling()
    lastSequenceRef.current = 0
    startContinueIntro()

    setState((current) => ({
      ...current,
      buttonText: "Mapping architecture",
      events: [],
      linksVisible: false,
      status: "Reading repository signals.",
      streamVisible: true,
      working: true
    }))

    fetch(architecture.paths.start, {
      method: "POST",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-store",
        "X-CSRF-Token": csrfToken()
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not start mapping.")
        return response.json()
      })
      .then((payload) => {
        if (!applyEventsPayload(payload)) startEventPolling()
      })
      .catch((error) => {
        failGeneration(error.message)
      })
  }

  const statusLine = state.events[state.events.length - 1]?.text || state.status
  const streamLines = useMemo(() => architectureThinkingLines({
    events: state.events,
    status: statusLine,
    working: state.working
  }), [state.events, state.working, statusLine])

  useEffect(() => {
    if (onStreamLinesChange) onStreamLinesChange(streamLines)
  }, [onStreamLinesChange, streamLines])

  const controlsDisabled = !visible
  const showReplayButton = visible && !state.working && (!architecture.available || developerToolsEnabled)

  return (
    <section
      className={`architecture-console ${visible ? "slide-up slide-up-delayed" : ""}`.trim()}
      data-architecture-panel
      hidden={!visible}
      aria-hidden={!visible}
    >
      <AgentActivityFeed
        events={state.events}
        generating={state.working}
        hidden={!state.streamVisible}
        lines={streamLines}
        orbState="solving"
        transcript
      />

      <div className="architecture-primary">
        <button
          type="button"
          className="welcome-primary-button architecture-continue-button"
          disabled={controlsDisabled || !continueEnabled}
          onClick={onContinue}
        >
          <span>Continue</span>
          {continueEnabled ? (
            <ForwardArrowIcon className="button-icon continue-arrow-icon" />
          ) : (
            <span className="continue-loading-dots" aria-hidden="true">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          )}
        </button>
        {showReplayButton ? (
          <button
            type="button"
            className="text-action-button architecture-restart-button"
            data-architecture-button
            data-architecture-url={architecture.paths.start}
            disabled={controlsDisabled || state.working}
            onClick={startGeneration}
          >
            {state.buttonText}
          </button>
        ) : null}
      </div>
    </section>
  )
}

export function architectureThinkingLines({
  compact = false,
  events = [],
  status = "Reading repository signals.",
  working = false
} = {}) {
  if (events.length === 0) {
    return [
      { active: false, key: "status", text: status, tone: "status" }
    ]
  }

  return events.map((event, index) => {
    const line = messageLineForEvent(event, { compact })
    if (!line) return null

    return {
      active: false,
      key: line.key || `${index}-${line.tone || "event"}`,
      text: line.text,
      tone: line.tone
    }
  }).filter(Boolean)
}

function messageLineForEvent(event, { compact = true } = {}) {
  const text = formatEventText(event?.text || event?.raw_type || event?.rawType || "", { compact })
  if (!text) return null

  return {
    key: event.key || `${event?.sequence || text}-${event?.type || "event"}`,
    text,
    tone: event?.type || "status"
  }
}

function normalizeEvent(event) {
  if (event?.type === "heartbeat") return null

  const activity = event?.activity || null
  const rawType = event?.raw_type || event?.rawType
  const text = architectureEventDeltaMergeKey(event) ?
    String(event?.text || "") :
    formatEventText(event?.text || rawType || (activity ? "" : "Conceptive event"), { compact: false })
  if (!text && !activity) return null

  return {
    activity,
    key: `${event?.sequence || text || activity?.label}-${event?.type || "event"}`,
    rawType,
    sequence: event?.sequence,
    text,
    type: event?.type || "status"
  }
}
